import { useQuery } from 'react-query';

// configs
import { API } from 'configs/api';
// keys
import { detailsQueryKeys } from './detailsQueryKeys';
// utils
import { LP_DESIGN_LOGO } from 'pages/landing/Footer';
import { LP_DESIGN_SOCIAL_MEDIA } from 'pages/landing/SocialIcons';
import { RepairOrderItem } from 'queries/approveOrder/types';
import { useLocation, useParams } from 'react-router';
import {
  AutomotiveLanding,
  CTASet,
  InventoryItem,
  LandingPageData,
  UserCalendar,
  VideoChapterItem,
} from 'types';
import { UserLanguage } from 'types/userLanguage';
import { WebsiteUrl } from 'types/websiteUrl';
import { getMergeCodeFromUrl } from 'utils/url';
import { ensureVisitorCode } from 'utils/ensureVisitorCode';
import { TranscriptionData } from 'queries/video/types';

export type InteractiveCTA = {
  title: string;
  url: string;
  isVisible: boolean;
  type: string;
  linkValue: string;
};

export interface PlaybackDetailsResponse {
  videoId: number;
  receiver: {
    phone: string | null;
    email: string | null;
    viewCount: number | null;
  };
  userId: number;
  customerId: number;
  video: Video;
  advisor: Advisor;
  mapIframeEmbedCode: string;
  automotiveLanding: AutomotiveLanding;
  landingPage: LandingPageData;
  annotations: AnnotationItem[];
  companyAnnotations: AnnotationItem[];
  isAutomotive: boolean;
  isAutomotiveSales: boolean;
  playerDesign: PlayerDesign;
  landingPageDesign: LandingPageDesign;
  viewerStatisticId: number;
  interactiveCTAs: InteractiveCTA[];
  reactionsEnabled: boolean;
  erid: string;
  useWebsiteOverlay: boolean;
  hidePoweredBy: boolean;
  socialLinks: SocialLink[];
  notifyUser: boolean;
  repairOrder: RepairOrderItem;
  inventoryItem: InventoryItem;
  calendar: UserCalendar;
  ctaSet: CTASet;
  websiteUrl?: WebsiteUrl;
  dealerSocketFormLink?: string;
  redirectShortUrl?: string;
  cdkEnterpriseId?: string | null;
  cdkStoreId?: string | null;
  customerLogo?: string;
  language?: UserLanguage;
  transcript?: TranscriptionData;
}

export interface Advisor {
  workingHours: WorkingHours;
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phone2: string;
  website: string;
  title: string;
  department: string;
  customerId: number;
  profilePictureUrl: string | null;
}

export interface WorkingHours {
  friday: null;
  monday: null;
  sunday: null;
  tuesday: null;
  saturday: null;
  thursday: null;
  wednesday: null;
}

export interface CompanyAnnotation {
  annotationId: number;
  customerId: number;
  position: string;
  imageUrl: string;
  imageName: string;
  imageOpacity: string;
  imageWidth: null;
  imageHeight: null;
  imageWidthPercentage: string;
  imageHeightPercentage: string;
  linkType: string;
  email: null | string;
  url: null | string;
  phone: null | string;
  createdAt: string;
}

type AnnotationItem = {
  annotationId?: string;
  customerId: number;
  type: string;
  startTime: number;
  endTime: number;
  startTimeText?: string;
  endTimeText?: string;
  linkType?: string;
  url?: string;
  email?: string;
  phone?: string;
  text?: string;
  text2?: string;
  imageOpacity?: string;
  imageUrl?: string;
  textColor?: string;
  isLinkEnabled?: boolean;
};

export interface LandingPage {
  thumbnail: string;
  userId: number;
  customerId: number;
  isDefault: number;
  isShared: number;
  isArchived: number;
  allowDuplicates: number;
  builderVersion: string;
  id: number;
  title: string;
  pageData: PageData;
  hidden: number;
  layoutName: string;
  styleName: string;
}

export interface PageData {
  pageDetails: PageDetails;
  selectedLayoutData: { [key: string]: SelectedLayoutData };
}

export interface PageDetails {
  backgroundColor: string;
  backgroundImageUrl: null;
  pageBackgroundSize: string;
  pageBackgroundPosition: string;
  selectedStyleProperties: SelectedStyleProperties;
}

export interface SelectedStyleProperties {
  fontStyle: string;
  textColor: string;
  fontFamily: string;
  fontWeight: string;
  backgroundColor: string;
}

export interface SelectedLayoutData {
  isVisible: boolean;
  sectionType: number;
  childSections: ChildSection[];
  sectionPosition: string;
  sectionDisplayName: string;
  sectionBackgroundColor: string;
  sectionBackgroundImageUrl: string;
  socialLinks?: SocialLinks;
  videoUrl?: string;
  videoPlayerProperties?: VideoPlayerProperties;
  isOverrideVideoSettings?: boolean;
}

export interface ChildSection {
  logoUrl?: string;
  isVisible: boolean;
  logoWidth?: string;
  logoHeight?: string;
  sectionType: number;
  childSections: any[];
  logoAspectRatio?: number;
  sectionPosition: string;
  widthPercentage?: number;
  sectionDisplayName: string;
  useImagePercentage?: boolean;
  sectionBackgroundColor: string;
  sectionBackgroundImageUrl: string;
  textColor?: string;
  textWidth?: string;
  textHeight?: string;
  textContent?: string;
  isUserContactInfo?: boolean;
  links?: any[];
  isVertical?: boolean;
  elementProperties?: AutomotiveLanding;
  elementContainerPosition?: string;
}

export interface SocialLinks {
  id: number;
  bni: string;
  yelp: string;
  tikTok: string;
  userId: number;
  zeemee: string;
  edmunds: string;
  twitter: string;
  youtube: string;
  facebook: string;
  linkedIn: string;
  whatsApp: string;
  instagram: string;
  pinterest: string;
  carsdotcom: string;
  dealerRater: string;
}

export interface VideoPlayerProperties {
  playerIconsColor: null;
  playerButtonPosition: string;
  playerBackgroundColor: null;
}

export enum LP_DESIGN_CATEGORIES {
  OEM = 'Oem Template',
  STOCK = 'Stock Template',
  CUSTOM = 'Custom Template',
}

export enum TEXT_ALIGNMENT {
  LEFT = 'left',
  RIGHT = 'right',
  CENTER = 'center',
}

export enum TEXT_ORIENTATION {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

export enum FONT_CASE_KEYWORD {
  CAPITALIZED = 'capitalized',
  LOWERCASE = 'lowercase',
  UPPERCASE = 'uppercase',
}

export enum FONT_WEIGHT_KEYWORD {
  THIN = '100',
  LIGHT = '300',
  REGULAR = '400',
  MEDIUM = '500',
  SEMIBOLD = '600',
  BOLD = '700',
  EXTRABOLD = '800',
}

export enum GRADIENT_TYPES {
  DARK = 'dark',
  LIGHT = 'light',
}

export const BODY_ORIENTATION = {
  left: 'left',
  center: 'center',
  right: 'right',
} as const;

export type BodyOrientationType =
  (typeof BODY_ORIENTATION)[keyof typeof BODY_ORIENTATION];

export const LAYOUT_PRESET = {
  custom: 'custom',
  fullScreen: 'fullScreen',
} as const;

export type LayoutPresetType =
  (typeof LAYOUT_PRESET)[keyof typeof LAYOUT_PRESET];

export const getLayoutPreset = (
  design?: Pick<LandingPageDesign, 'layoutPreset'> | null
): LayoutPresetType => design?.layoutPreset ?? LAYOUT_PRESET.custom;

export interface LandingPageDesign {
  title: string;
  thumbnail: string;
  category: LP_DESIGN_CATEGORIES;
  fontFamily: string;
  bodyFontSize: number;
  bodyTextColor: string;
  bodyBackgroundColor: string;
  textColor: string;
  additionalTextBackgroundColor: string;
  additionalTextColor: string;
  additionalTextFontFamily: string;
  bodyOrientation: BodyOrientationType;
  buttonColor: string;
  buttonHoverColor: string;
  showButtonBorder: number;
  buttonFontCase: FONT_CASE_KEYWORD;
  buttonFontWeight: FONT_WEIGHT_KEYWORD;
  buttonFontFamily: string;
  buttonFontSize: number;
  buttonLineHeight: number;
  buttonCornerRadius: number;
  buttonPaddingY: number;
  buttonPaddingX: number;
  buttonBorderColor: string;
  buttonBorderHoverColor: string;
  buttonTextColor: string;
  buttonTextHoverColor: string;
  gradient: GRADIENT_TYPES;
  showGradient: number;
  additionalText: string;
  headerBackgroundColor: string;
  headerTextColor: string;
  titleFontSize: number; // headerFontSize
  headerFontCase: FONT_CASE_KEYWORD;
  headerFontWeight: FONT_WEIGHT_KEYWORD;
  headerFontFamily: string;
  headerLineHeight: number;
  headerLogoMaxWidth: number;
  headerLogoMaxHeight: number;
  showContactIcons: number;
  showFirstName: number;
  showLastName: number;
  showPhone1: number;
  showPhone2: number;
  showDepartment: number;
  showTitle: number;
  showWebsite: number;
  showEmail: number;
  showProfilePicture: number;
  showHeaderShadow: number;
  showFooterShadow: number;
  headerContentAlignment: TEXT_ALIGNMENT;
  headerContentOrientation: TEXT_ORIENTATION;
  footerBackgroundColor: string;
  footerLogo: LP_DESIGN_LOGO;
  showSocialMediaIcons: number;
  socialMediaIconType?: LP_DESIGN_SOCIAL_MEDIA;
  headerLogo: string;
  bodyBackgroundImage: string;
  layoutPreset?: LayoutPresetType;
  id: number;
}

export interface PlayerDesign {
  playButtonPosition: string;
  playerBackgroundColor: string;
  playerIconsAndTextColor: string;
  thumbnail: string;
}

export interface SocialLink {
  path: string;
  name: string;
}

export interface Video {
  id: string;
  thumbnail: string;
  videoSource: string;
  videoLength: number;
  protected: number;
  chapters: VideoChapterItem[] | undefined;
  videoType: string | null;
  customerName: string | null;
  title: string;
  folder: string | null;
}

export const getPlaybackDetails = async ({
  sample,
  shortUrl,
}: {
  sample: boolean;
  shortUrl: string;
}): Promise<PlaybackDetailsResponse> => {
  const response = await API.get(`/playback/${shortUrl}/details`, {
    params: { sample, email: getMergeCodeFromUrl() },
  });
  return response.data;
};

export const usePlaybackDetailsQuery = () => {
  const { shortUrl } = useParams();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const sample = urlParams.get('sample') !== null;
  if (!shortUrl) {
    throw new Error('Invalid URL');
  }

  return useQuery(
    detailsQueryKeys.all(),
    () => getPlaybackDetails({ sample, shortUrl }),
    {
      enabled: !!shortUrl,
      onSuccess(data) {
        if (data.reactionsEnabled) {
          ensureVisitorCode();
        }
      },
    }
  );
};
